import {RegisterOptions, UseFormGetValues, Validate} from "react-hook-form";
import {
    belongsToAssessmentReportFamily,
    ClientReportSubmitClientOfferHourInput,
    ClientReportSubmitClinicalRecommendationHourInput,
    ClientReportSubmitClinicalRecommendationInput,
    HourField,
    ReportServiceType,
    requireServiceType,
    serviceTypeMappedToHourFields
} from "../../domain";
import {ChangeHandler} from "react-hook-form/dist/types/form";
import _ from "lodash";

const max2DecimalsRegexp = /^\d*(\.\d{1,2})?$/;

export const hourInputValidationRules: RegisterOptions = {
    required: {
        value: true,
        message: 'Required'
    },
    min: {
        message: 'Negative number is not allowed.',
        value: 0,
    },
    max: {
        message: 'Please enter at most 5 digits (including 2 decimals).',
        value: 720, // 1 month
    },
    validate: {
        isAnHourNumber: (v: number) => {
            if (v == null) {
                return true;
            }
            if (!max2DecimalsRegexp.test(v.toString())) {
                return 'Only support up to 2 decimals';
            }
            return true;
        },
    },
    // input is type="text", this method ensures the view-model receives either a valid number or null
    setValueAs: v =>
        (v == null || v.toString().trim() === '') ? null : (isNaN(Number(v)) ? null : Number(v)),
};

export const stopNonNumericInput: ChangeHandler = async (event) => {
    if (isNaN(Number(event.target.value))) {
        event.target.value = '';
        return true;
    }
    return false;
};

type minMaxFields = [keyof ClientReportSubmitClinicalRecommendationHourInput, keyof ClientReportSubmitClinicalRecommendationHourInput];
export const offeredHourRangeMap: Map<keyof ClientReportSubmitClientOfferHourInput, Array<minMaxFields>> = new Map([
    [
        'offeredParaprofessionalHours',
        [
            ['practitionerParaprofessionalHoursMin', 'practitionerParaprofessionalHoursMax']
        ]
    ],
    [
        'offeredMidLevelHours',
        // BHPNPP-1877 either range will work
        [
            ['caregiverMidLevelHoursMin', 'caregiverMidLevelHoursMax'],
            ['practitionerMidLevelHoursMin', 'practitionerMidLevelHoursMax'],
        ]
    ],
    [
        'offeredHighLevelHours',
        [
            ['caregiverHighLevelHoursMin', 'caregiverHighLevelHoursMax'],
            ['practitionerHighLevelHoursMin', 'practitionerHighLevelHoursMax']
        ]
    ],
    [
        'offeredGroupCareHours',
        [['practitionerGroupCareHoursMin', 'practitionerGroupCareHoursMax']]
    ],
]);
offeredHourRangeMap.set('currentOfferedParaprofessionalHours', offeredHourRangeMap.get('offeredParaprofessionalHours')!);
offeredHourRangeMap.set('currentOfferedMidLevelHours', offeredHourRangeMap.get('offeredMidLevelHours')!);
offeredHourRangeMap.set('currentOfferedHighLevelHours', offeredHourRangeMap.get('offeredHighLevelHours')!);
offeredHourRangeMap.set('currentOfferedGroupCareHours', offeredHourRangeMap.get('offeredGroupCareHours')!);

function getPermittedHourFields(serviceTypes: ReportServiceType[]) {
    return _.flatMap(serviceTypes, (serviceType) => serviceTypeMappedToHourFields.get(serviceType));
}

function getPermittedHourFieldsByCustomMapping(serviceTypes: ReportServiceType[], customizePermittedFields: Map<ReportServiceType, HourField[]>) {
    return _.flatMap(serviceTypes, (serviceType) => customizePermittedFields.get(serviceType));
}

export function validateOfferedHour(
    field: keyof ClientReportSubmitClientOfferHourInput,
    offeredHourLabel: string,
    getValues: UseFormGetValues<ClientReportSubmitClinicalRecommendationInput>,
    noMinValidation: boolean,
    allowZero: boolean = false,
): Validate<number | null> {
    const minMaxRanges = offeredHourRangeMap.get(field);
    if (minMaxRanges == null || minMaxRanges.length === 0) {
        throw new SyntaxError(`"${field}" does not have a range map`);
    }
    return function validate (offeredHour: number | null) {
        if (offeredHour == null) {
            return true;
        }
        if (allowZero && offeredHour === 0) {
            return true;
        }
        const serviceTypes = getValues('serviceType') as ReportServiceType[];

        // BHPNPP-1877 allow 0 offered paraprofessional hrs for flexi
        const isFlexibleTreatment = serviceTypes.includes(ReportServiceType.ThreeTierOrParentLed)
            || serviceTypes.includes(ReportServiceType.ThreeTierOrParentTraining);
        const isParaproffesionalOfferedHrs = field === 'offeredParaprofessionalHours' || field === 'currentOfferedParaprofessionalHours';
        if (isFlexibleTreatment && isParaproffesionalOfferedHrs && offeredHour === 0) {
            return true;
        }

        const permittedFields = getPermittedHourFields(serviceTypes);

        // BHPNPP-1877 only needs to pass at least one range
        let isWithinRange = false;
        let hasARange = false; // both min-max not null

        // BHPNPP-1854 allow null min, but offer must be equal to max
        let matchingMax = false;

        for (const [minField, maxField] of minMaxRanges)  {
            if (!(permittedFields?.includes(minField) && permittedFields?.includes(maxField))) {
                continue;
            }
            const min = getValues(minField);
            const max = getValues(maxField);
            if (min != null && max != null) {
                hasARange = true;
                isWithinRange = min >= 0 && max >= 0 && (noMinValidation || min <= offeredHour) && (offeredHour <= max);
            } else if (max != null) {
                matchingMax = offeredHour === max;
            }

            if (isWithinRange || matchingMax) {
                return true;
            }
        }

        if (hasARange) {
            return `${offeredHourLabel} must be within range of Clinically Recommended Hours`;
        } else {
            return `${offeredHourLabel} needs to match the clinical recommended hours`;
        }
    }
}

export function shouldShowHourInputs(serviceTypes: ReportServiceType[],
                                     showingHours: HourField[],
                                     additionalPermittedMap?: Map<ReportServiceType, HourField[]>): boolean {
    let permitted = getPermittedHourFields(serviceTypes);
    if (permitted == null) {
        return false;
    }
    if (additionalPermittedMap) {
        const additionalPermitted = getPermittedHourFieldsByCustomMapping(
            serviceTypes,
            additionalPermittedMap);
        permitted = [...new Set([...permitted, ...additionalPermitted])];
    }
    return showingHours.some((hourField) => permitted.includes(hourField));
}

export function hasClinicalRecommendationMaxHours(
    hours: Pick<ClientReportSubmitClinicalRecommendationHourInput,
        | 'caregiverMidLevelHoursMax'
        | 'caregiverHighLevelHoursMax'
        | 'practitionerParaprofessionalHoursMax'
        | 'practitionerMidLevelHoursMax'
        | 'practitionerHighLevelHoursMax'
        | 'practitionerGroupCareHoursMax'>,
    serviceTypes: ReportServiceType[],
): boolean {
    const clinicalMaxFields: Array<keyof typeof hours> = [
        'caregiverMidLevelHoursMax',
        'caregiverHighLevelHoursMax',
        'practitionerParaprofessionalHoursMax',
        'practitionerMidLevelHoursMax',
        'practitionerHighLevelHoursMax',
        'practitionerGroupCareHoursMax',
    ];

    const displayableMaxFields = clinicalMaxFields.filter((field) => {
        if (!shouldShowHourInputs(serviceTypes, [field])) {
            return false;
        }
        // mid-level Max may be blank for parent-led / parent training
        if (field === 'practitionerMidLevelHoursMax'
            && serviceTypes.some((st) => st === ReportServiceType.Parent_Led_ABA || st === ReportServiceType.ABA_Parent_Training)) {
            return false;
        }
        return true;
    });

    if (displayableMaxFields.length === 0) {
        return false;
    }

    return displayableMaxFields.some((field) => hours[field] != null);
}

export function shouldRenderClientOfferedHours(reportType: string, serviceTypes: ReportServiceType[]) {
    if (!requireServiceType(reportType)) {
        return false;
    }

    return shouldShowHourInputs(serviceTypes, [
        'offeredParaprofessionalHours',
        'offeredMidLevelHours',
        'offeredHighLevelHours',
        'offeredGroupCareHours',
    ]);
}

export function shouldShowServiceRecommendedInput(reportType: string, isForStaffingPlan: boolean) {
    return !isForStaffingPlan && belongsToAssessmentReportFamily(reportType);
}
